import React, { useEffect, useRef } from "react";
import { TextField } from "@mui/material";
import { ModalWrapper } from "../../../../../shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";

interface AddCarModalProps {
  open: boolean;
  carNumber: string;
  onCarNumberChange: (value: string) => void;
  onClose: () => void;
  onAdd: () => void;
}

export const AddCarModal: React.FC<AddCarModalProps> = ({ open, carNumber, onCarNumberChange, onClose, onAdd }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // ✅ Auto-focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // ✅ Handle Esc key globally when modal is open
  useEffect(() => {
    if (!open) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open]);

  if (!open) return null;

  // ✅ Handle input changes
  const handleCarNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    if (/^[A-Za-z0-9\s-]*$/.test(value)) {
      onCarNumberChange(value);
    }
  };

  const isCarNumberValid = carNumber.trim().length >= 5 && carNumber.trim().length <= 13;

  // ✅ Press Enter to add
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isCarNumberValid) {
      e.preventDefault();
      onAdd();
    }
  };

  // ✅ Cancel logic (clear input + close)
  const handleCancel = () => {
    onCarNumberChange("");
    onClose();
  };

  return (
    <ModalWrapper title="Add Car Number" onClose={handleCancel} width="max-w-md">
      <div className="flex flex-col gap-4">
        <TextField
          inputRef={inputRef}
          label="Car Number *"
          variant="outlined"
          fullWidth
          value={carNumber}
          onChange={handleCarNumberChange}
          onKeyDown={handleKeyDown}
          inputProps={{
            minLength: 5,
            maxLength: 13,
            pattern: "[A-Za-z0-9\\s-]*",
            style: { textTransform: "uppercase" },
          }}
          error={carNumber.length > 0 && !isCarNumberValid}
        />

        <div className="flex justify-end gap-3 mt-4">
          <CommonButtons label="Cancel" className="w-32" variant="outlineDanger" onClick={handleCancel} />
          <CommonButtons label="Add" className="w-32" onClick={onAdd} disabled={!isCarNumberValid} />
        </div>
      </div>
    </ModalWrapper>
  );
};
